import * as React from 'react';
import { Modal, message, Spin } from 'antd';
import I18N from 'src/services/I18NService';
import { ColumnProps } from 'antd/lib/table';
import PriceChangeService, { IPriceApprove, IPriceChangeParam, EnumPriceType } from 'src/services/PriceChangeService';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import CommonTable from './CommonTable';
import { ICommonTable } from './CommonTable';
import { EditableComponents, IEditColumnProps } from './Editable';
import * as uuid from 'uuid';
import { MddCurrency } from '@macrocura/utils';

interface IPriceChangeTableProps {
  userInfo: IAgentInfo | IDrugStoreInfo;
  /** 选中的key列表 */
  selectedRowKeys: string[];
  /** 是否可编辑 */
  edit: boolean;
  /** 是否显示Modal */
  visible: boolean;
  /** 数据加载中Spin */
  loading: boolean;
  /** 改价的数据记录 */
  dataSource?: IPriceApprove[];
  /** 关闭Modal回调 */
  cbVisible(visible: boolean): void;
}

interface IPriceChangeTableState extends ICommonTable<IPriceApprove> {
  confirmLoading: boolean;
  columns: Array<IEditColumnProps<IPriceApprove>>;
  recordData: IPriceApprove[] | undefined;
}

class PriceChangeTable extends React.Component<IPriceChangeTableProps, IPriceChangeTableState> {
  constructor(props: IPriceChangeTableProps) {
    super(props);

    const currencySymbol = MddCurrency.getCurrencySymbol(this.props.userInfo.currency);
    const columns: Array<IEditColumnProps<IPriceApprove>> = [
      {
        title: I18N.get('med_agent_name'),
        key: 'medicine_name',
        dataIndex: 'medicine.name',
        width: 150,
        align: 'center',
      },
      {
        title: `${I18N.get('med_change_original_supply_price')}(${currencySymbol})`,
        key: 'original_supply_price',
        dataIndex: 'medicine.supply_price',
        width: 150,
        align: 'center',
      },
      {
        title: `${I18N.get('med_change_supply_price')}(${currencySymbol})`,
        key: 'new_supply_price',
        dataIndex: 'new_supply_price',
        width: 200,
        align: 'center',
        className: 'new-price',
        editable: this.props.edit,
        render: (text: any, record: IPriceApprove, index: number): React.ReactNode => {
          if (!record.item) {
            return record.new_supply_price || record.medicine.supply_price;
          } else {
            return this.showNewPrice(record, EnumPriceType.SUPPLY_PRICE);
          }
        },
      },
      {
        title: `${I18N.get('med_change_original_floor_price')}(${currencySymbol})`,
        key: 'original_floor_price',
        dataIndex: 'medicine.floor_price',
        width: 150,
        align: 'center',
      },
      {
        title: `${I18N.get('med_change_buy_floor_price')}(${currencySymbol})`,
        key: 'new_floor_price',
        dataIndex: 'new_floor_price',
        width: 200,
        align: 'center',
        className: 'new-price',
        editable: this.props.edit,
        render: (text: any, record: IPriceApprove, index: number): React.ReactNode => {
          if (!record.item) {
            return record.new_floor_price || record.medicine.floor_price;
          } else {
            return this.showNewPrice(record, EnumPriceType.FLOOR_PRICE);
          }
        },
      },
    ];
    this.state = {
      rowKey: 'rowKey',
      confirmLoading: props.loading,
      columns,
      recordData: props.dataSource,
      loadingTable: props.loading,
    };
  }

  showNewPrice = (record: IPriceApprove, type: EnumPriceType): string | number => {
    let show;
    if (record.hasOwnProperty('is_approving')) {
      const detail = (record.item.details || []).find((i) => i.price_type === type);
      if (!!detail) {
        if (!!record.is_approving) {
          show = I18N.get('med_change_exist_record');
        } else {
          show = detail.new_price;
        }
      } else {
        show = '~';
      }
    } else {
      const detail = (record.item.details || []).find((i) => i.price_type === type);
      if (!!detail) {
        show = I18N.get('med_change_exist_record');
      } else {
        show = '~';
      }
    }
    return show;
  };

  getNewPrice = (data: IPriceApprove, type: EnumPriceType): number => {
    let newPrice = 0;
    const priceData = (data.item.details || []).find((item) => item.price_type === type);
    if (priceData) {
      newPrice = priceData.new_price;
    }
    return newPrice;
  };

  async componentDidMount() {
    await this.fetchPriceChangeData();
  }

  UNSAFE_componentWillReceiveProps(nextProps: IPriceChangeTableProps) {
    this.setState(
      {
        recordData: nextProps.dataSource,
        confirmLoading: nextProps.loading,
        loadingTable: nextProps.loading,
      },
      async () => {
        await this.fetchPriceChangeData();
      }
    );
  }

  componentWillUnmount() {
    this.setState = () => {
      return;
    };
  }

  fetchPriceChangeData = async () => {
    if (this.state.loadingTable) {
      return;
    }
    try {
      this.setState({
        loadingTable: true,
      });
      if (this.props.selectedRowKeys.length > 0) {
        const res = await PriceChangeService.getPriceChangeDetailData(this.props.selectedRowKeys);
        if (!!res) {
          this.setState({
            recordData: res,
            loadingTable: false,
          });
        }
      }
    } catch (error) {
      this.setState({
        loadingTable: false,
      });
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_delete_failure'));
      }
    }
  };

  handleOk = async () => {
    if (this.state.confirmLoading) {
      return;
    }
    const { recordData } = this.state;
    const priceChangeError = (recordData || []).filter((record) => {
      return !record.item && (record.new_floor_price || record.medicine.floor_price) > (record.new_supply_price || record.medicine.supply_price);
    });
    if (priceChangeError.length > 0) {
      const errorMedicineName = priceChangeError.map((item) => item.medicine.name).join(',');
      message.error(errorMedicineName + ': ' + I18N.get('med_buy_price') + I18N.get('med_not_lower') + I18N.get('med_preserve_price'), 4);
      return;
    }
    this.setState({
      confirmLoading: true,
    });
    try {
      if (!!recordData && !!recordData.length) {
        const dataList: IPriceChangeParam[] = [];
        for (const item of recordData) {
          const priceChangeDetail: IPriceChangeParam = {
            sku: item.medicine.uuid,
            details: [],
          };
          if (item.hasOwnProperty('is_approving')) {
            if (!item.is_approving) {
              if (!!(item.item.details || []).find((t) => t.price_type === EnumPriceType.SUPPLY_PRICE)) {
                priceChangeDetail.details.push(this.createPriceChangeDetail(item, EnumPriceType.SUPPLY_PRICE));
              }

              if (!!(item.item.details || []).find((t) => t.price_type === EnumPriceType.FLOOR_PRICE)) {
                priceChangeDetail.details.push(this.createPriceChangeDetail(item, EnumPriceType.FLOOR_PRICE));
              }
            }
          } else {
            if (!!item.new_supply_price && item.new_supply_price !== item.medicine.supply_price) {
              priceChangeDetail.details.push({
                price_type: EnumPriceType.SUPPLY_PRICE,
                original_price: item.medicine.supply_price,
                new_price: item.new_supply_price,
              });
            }
            if (!!item.new_floor_price && item.new_floor_price !== item.medicine.floor_price) {
              priceChangeDetail.details.push({
                price_type: EnumPriceType.FLOOR_PRICE,
                original_price: item.medicine.floor_price,
                new_price: item.new_floor_price,
              });
            }
          }
          if (!!priceChangeDetail.details.length) {
            dataList.push(priceChangeDetail);
          }
        }

        if (!!dataList.length) {
          await PriceChangeService.priceChangeFloorSupply(dataList);
          message.success(I18N.get('common_submit_success'));
        }
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_submit_failure'));
      }
    } finally {
      this.setState({
        confirmLoading: false,
      });
      this.props.cbVisible(false);
    }
  };

  createPriceChangeDetail = (item: IPriceApprove, type: EnumPriceType) => {
    const priceType = type === EnumPriceType.FLOOR_PRICE ? 'floor_price' : 'supply_price';
    return {
      price_type: type,
      original_price: item.medicine[priceType],
      new_price: this.getNewPrice(item, type),
    };
  };

  handleCancel = () => {
    this.props.cbVisible(false);
  };

  handleSave = async (row: IPriceApprove) => {
    try {
      if (!!row.item) {
        return;
      }
      if (row.hasOwnProperty('new_supply_price')) {
        if (!row.new_supply_price || Number(row.new_supply_price) < 0.0001) {
          message.error(I18N.get('err_amount_minus2'));
          return;
        }
      }

      if (row.hasOwnProperty('new_floor_price')) {
        if (!row.new_floor_price || Number(row.new_floor_price) < 0.0001) {
          message.error(I18N.get('err_amount_minus2'));
          return;
        }
      }

      // if ((row.new_floor_price || row.medicine.floor_price) > (row.new_supply_price || row.medicine.supply_price)) {
      //   message.error(I18N.get('med_buy_price') + I18N.get('med_not_lower') + I18N.get('med_preserve_price'));
      //   return ;
      // }

      if (!!this.state.recordData) {
        const index = this.state.recordData.findIndex((t) => t.rowKey === row.rowKey);
        this.state.recordData[index] = row;
      }
      this.setState({ recordData: this.state.recordData });
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_save_failure'));
      }
    }
  };

  render() {
    const columns: Array<ColumnProps<IPriceApprove>> = this.state.columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: IPriceApprove) => ({
          record,
          editable: col.editable && !record.item,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave: this.handleSave,
        }),
      };
    });

    const dataSource = !!this.state.recordData
      ? this.state.recordData.map((t) => {
          return Object.assign(t, {
            rowKey: uuid.v4(),
          });
        })
      : [];

    const { loading } = this.props;
    const { loadingTable } = this.state;

    return (
      <Modal
        title={I18N.get('med_change_price')}
        okText={I18N.get('common_submit')}
        cancelText={I18N.get('common_cancel')}
        onOk={this.handleOk}
        confirmLoading={this.state.confirmLoading}
        onCancel={this.handleCancel}
        closable={false}
        centered={true}
        visible={this.props.visible}
        maskClosable={false}
        width={1000}
        key="price-change-modal"
      >
        <Spin tip="加载中..." spinning={loading}>
          <CommonTable
            rowKey={this.state.rowKey}
            showPagination={false}
            loadingTable={loadingTable}
            components={EditableComponents}
            rowClassName={() => 'editable-row'}
            columns={columns}
            dataSource={dataSource}
            scroll={{ y: 420 }}
            locale={{
              emptyText: `${I18N.get('med_change_no_have')}`,
            }}
          />
          <div style={{ color: '#555', fontSize: '11px', lineHeight: '18px', marginTop: '24px', textAlign: 'right' }}>{I18N.get('med_change_record_tips')}</div>
        </Spin>
      </Modal>
    );
  }
}

export default PriceChangeTable;



// WEBPACK FOOTER //
// ./src/components/common/PriceChangeTable.tsx